import { BulkTimePickers } from '@bo-components/simulations/steps/StepThreeFinalize/BulkTimePickers';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import type { OddsResponse } from '@library/types/simulations';
import { ProjectionRows } from '@bo-components/simulations/steps/StepThreeFinalize/ProjectionRows';
import { useLeagues } from '@bo-hooks/useLeagues';
import { useSimulations } from '@bo-hooks/simulations/useSimulations';

export interface StepThreeFinalizeProps {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  existingProjections?: Array<GamePlayerProjectionTrading>;
  isSimulationExistingProjectionsError: boolean;
  isSimulationExistingProjectionsLoading: boolean;
  isSimulationOddsError: boolean;
  isSimulationOddsLoading: boolean;
  leagueId: string;
  newAndChangedProjections: Array<GamePlayerProjectionTrading>;
  simulationGeneratedProjections?: Array<GamePlayerProjectionTrading>;
  simulationOdds?: OddsResponse;
  statTypes?: Array<LeagueProjectionAttributes>;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: GamePlayerProjectionTrading;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
}
// TODO - Add more context about sim projections with an existing projection

export const StepThreeFinalize = ({
  allMarkets,
  existingProjections = [],
  isSimulationExistingProjectionsError,
  isSimulationExistingProjectionsLoading,
  isSimulationOddsError,
  isSimulationOddsLoading,
  leagueId,
  newAndChangedProjections,
  simulationGeneratedProjections = [],
  simulationOdds = undefined,
  statTypes = [],
  updateProjection
}: StepThreeFinalizeProps) => {
  const { leagueInfo } = useLeagues({
    initialId: leagueId
  });

  const { handleBulkChangeProjectionTime } = useSimulations({});

  const currentLeagueFilters = leagueInfo?.baseLeagueFilters.find(
    (leagueFilters) => leagueFilters.leagueId === leagueId
  );

  if (isSimulationOddsError) {
    return <NoResults label="Odds Error" />;
  }

  if (isSimulationOddsLoading || !simulationOdds) {
    return <Loading loadingMessage="Loading Odds" />;
  }

  if (isSimulationExistingProjectionsLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  if (isSimulationExistingProjectionsError || !leagueInfo) {
    return <NoResults label="Error loading League" />;
  }

  if (!currentLeagueFilters) {
    return <NoResults label="Error loading League" />;
  }

  return (
    <div className="bg-soFresh-110 flex flex-col gap-4 p-6 pt-4">
      <div className="flex items-center justify-between gap-4">
        <span className="text-soClean-100 font-heebo text-base font-medium leading-6">
          Finalize Projections
        </span>
      </div>
      <BulkTimePickers
        isTopLevel
        simProjections={simulationGeneratedProjections}
        updateProjection={updateProjection}
        onBulkTimeChange={handleBulkChangeProjectionTime}
      />
      {allMarkets.map((market) => {
        const marketExistingProjections = existingProjections.filter(
          (projection) => projection.statTypeId === String(market.stat_type_id)
        );

        const marketSimulationGeneratedProjections =
          simulationGeneratedProjections.filter(
            (projection) =>
              projection.statTypeId === String(market.stat_type_id)
          );

        return (
          <ProjectionRows
            key={market.market}
            market={market}
            marketExistingProjections={marketExistingProjections}
            newAndChangedProjections={newAndChangedProjections}
            onBulkTimeChange={handleBulkChangeProjectionTime}
            simulationGeneratedProjections={
              marketSimulationGeneratedProjections
            }
            statTypes={statTypes}
            updateProjection={updateProjection}
          />
        );
      })}
    </div>
  );
};
